import React, { useState, useRef } from "react"
import { Autocomplete, useLoadScript } from "@react-google-maps/api"
import styled from "styled-components"

import { useScrollPosition } from "./useScrollPosition"
import { useStateValue } from "../data/StateContext"
import { hex2rgba } from "../styles/tools"

const Wrapper = styled.div`
  position: fixed;
  z-index: 99;
  left: 0;
  bottom: 0;

  padding: 0.5rem 1rem 1rem;
  width: 100%;
  max-width: 100%;

  background-color: ${props => props.theme.color.darkBg};
  transition: all 0.2s ease-in-out, transform 0.5s ease-in-out;

  &[data-hidden="true"] {
    transform: translateX(-100%);
  }

  @media (min-width: 900px) {
    display: none;
  }

  h1 {
    display: block;
    margin: 0 0 0.675rem;

    text-align: center;
    font-size: 1.325rem;
  }
`

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;

  display: block;
  margin: 0;
  padding: 0;
  width: 2.5rem;
  height: 2rem;

  font-size: 1.4rem;
  font-weight: bold;
  color: ${props => props.theme.color.primary};
  text-align: center;

  background-color: ${props => props.theme.color.darkBg};
  transition: all 0.2s ease-in-out;

  border: none;
`

const OpenButton = styled.button`
  position: absolute;
  z-index: 98;
  bottom: 1rem;
  left: calc(100% + 1rem);

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  width: 5rem;
  height: 5rem;

  border-radius: 5rem;
  border: none;

  text-align: center;
  background-color: ${props => props.theme.color.darkBg};
  transition: all 0.2s ease-in-out;

  box-shadow: 2px 2px 10px ${props => hex2rgba(props.theme.color.primary, 0.3)};

  .marker {
    transition: all 0.2s ease-in-out;
    fill: ${props => props.theme.color.primary};
  }
  .shadow {
    transition: all 0.2s ease-in-out;
    fill: ${props => props.theme.color.mediumBg};
    fill-opacity: 0.3;
  }

  &[data-visible="true"] {
    background-color: ${props => props.theme.color.primary};

    .marker {
      fill: ${props => props.theme.color.lightBg};
    }
    .shadow {
      fill: ${props => props.theme.color.darkBg};
    }
  }
`

const Form = styled.form`
  position: relative;
  display: block;
  margin-bottom: 0;

  input[type="text"],
  button {
    display: block;

    font-size: 1.2em;
  }

  input[type="text"] {
    padding: 0.25rem 4rem 0.25rem 0.75rem;
    width: 100%;
    max-width: 100%;

    color: ${props => props.theme.color.primary};
    font-weight: bold;

    border: 3px solid ${props => props.theme.color.lightBg};
    border-radius: 200px;
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.theme.color.lightBg},
      0 0 0 6px ${props => props.theme.color.primaryBackground};

    background-color: ${props => props.theme.color.lightBg};
    -webkit-appearance: none;

    transition: all 0.2s ease-in-out;

    &:focus,
    &:focus-within {
      box-shadow: 0 0 0 3px ${props => props.theme.color.lightBg},
        0 0 0 6px ${props => props.theme.color.primaryBackground},
        0 0 0 12px ${props => hex2rgba(props.theme.color.highlight, 0.75)};
    }

    &::placeholder {
      font-style: italic;
    }
  }
  button {
    position: absolute;
    right: 0;
    top: 0;

    padding: 0.5rem 1.25rem 0.5rem 0.75rem;
    height: 100%;

    cursor: pointer;
    border: none;
    border-radius: 0 200px 200px 0;
    background-color: ${props => props.theme.color.primaryBackground};
    transition: all 0.2s ease-in-out;

    box-shadow: -9px 0 0 0 ${props => props.theme.color.heroButtonBg1},
      -18px 0 0 0 ${props => props.theme.color.heroButtonBg2},
      -27px 0 0 0 ${props => props.theme.color.heroButtonBg3};

    font-weight: bold;
    text-transform: uppercase;
    color: ${props => props.theme.color.secondary};

    &:hover {
      color: #fff;
    }
  }
`

let geocoder = null
const libraries = ["places"]

export default function Hero() {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.GATSBY_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
    preventGoogleFontsLoading: true,
  })

  const [hasInteracted, setHasInteracted] = useState(false)
  const [isHidden, setIsHidden] = useState(false)

  const [{ finderLoadingResults }, dispatch] = useStateValue()

  const submitForm = e => {
    e.preventDefault()
    if (!finderLoadingResults) {
      onPlaceChanged()
    }
  }

  const [autocomplete, setAutocomplete] = useState(null)
  const onAutocompleteLoad = a => {
    setAutocomplete(a)
  }

  let inputRef = useRef(null)

  useScrollPosition(({ currPos }) => {
    const newHiddenStatus = currPos.y <= -100
    if (!hasInteracted && newHiddenStatus) {
      setHasInteracted(true)
      setIsHidden(newHiddenStatus)
    }
  })

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace()
      if (inputRef) {
        inputRef.blur()
      }
      if (place && place.geometry) {
        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        updatePlace(lat, lng)
        // console.log("Found a place", lat, lng)
      } else {
        const backupValue = inputRef && inputRef.value ? inputRef.value : ""
        const searchLocation = place && place.name ? place.name : backupValue
        // console.log("Geocode a place", searchLocation)
        geocodeLocation(searchLocation)
      }
    }
  }

  const geocodeLocation = location => {
    if (geocoder !== null) {
      geocoder.geocode({ address: location }, function(results, status) {
        if (status === "OK") {
          // console.log("GEOCODE SUCCESS!", results)
          const lat = results[0].geometry.location.lat()
          const lng = results[0].geometry.location.lng()
          updatePlace(lat, lng)
          // console.log("Geocoded!", lat, lng)
        }
      })
    } else {
      console.warn("NO GEOCODER!")
    }
  }

  const updatePlace = (latitude, longitude) => {
    dispatch({
      type: "getFinderResults",
      finderLoadingResults: true,
      finderSearchTerm: inputRef.value,
      finderSearchLat: latitude,
      finderSearchLng: longitude,
    })
  }

  const renderInput = () => {
    const newGeocoder = new window.google.maps.Geocoder()
    geocoder = newGeocoder

    return (
      <Wrapper data-hidden={isHidden}>
        <CloseButton
          onClick={e => {
            setIsHidden(true)
          }}
        >
          &times;
        </CloseButton>

        <OpenButton
          data-visible={isHidden}
          onClick={e => {
            setIsHidden(false)
          }}
        >
          <svg
            width="45px"
            height="65px"
            viewBox="0 0 45 65"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
          >
            <ellipse
              className="shadow"
              fill="#7AACC7"
              cx="22.5"
              cy="59"
              rx="12.5"
              ry="4"
            ></ellipse>
            <path
              className="marker"
              d="M41.5833977,18.0950413 C39.6745163,8.59504132 31.809925,2 22.570939,2 C21.2728997,2 19.9748604,2.15702479 18.676821,2.39256198 C13.5610189,3.41322314 9.20876931,6.39669421 6.3072696,10.8719008 C3.40576989,15.3471074 2.33679631,20.6859504 3.40576989,26.0247934 C4.09296719,29.5578512 5.46736179,32.8553719 6.76540113,35.6033058 C9.28512457,41.0991736 12.5684006,46.6735537 17.0733606,53.268595 C18.065979,54.6818182 19.0585973,56.0950413 20.1275709,57.5082645 C20.1275709,57.5082645 20.1275709,57.5082645 20.1275709,57.5867769 L20.3566366,57.9008264 C20.3566366,57.9008264 20.4329919,57.9793388 20.4329919,57.9793388 C20.9674787,58.6859504 21.7310312,59 22.4945838,59 C23.0290706,59 23.9453336,58.7644628 24.7852415,57.7438017 C27.8394517,53.661157 30.4355304,49.6570248 32.8788985,45.6528926 C35.6276878,41.0991736 38.987319,35.053719 41.0489109,28.3016529 C42.1178845,24.8471074 42.270595,21.392562 41.5833977,18.0950413 Z M38.3001217,27.4380165 C36.3148851,33.8760331 33.0316091,39.6859504 30.4355304,44.0826446 C28.0685174,48.0082645 25.4724388,51.8553719 22.570939,55.8595041 C22.570939,55.8595041 22.570939,55.9380165 22.4945838,55.9380165 L22.4182285,55.7809917 C22.4182285,55.7809917 22.4182285,55.7024793 22.3418733,55.7024793 L22.3418733,55.7024793 C21.349255,54.2892562 20.3566366,52.8760331 19.3640183,51.4628099 C14.9354135,45.0247934 11.7284927,39.607438 9.28512457,34.268595 C8.06344048,31.677686 6.76540113,28.6157025 6.15455909,25.3181818 C5.23829602,20.7644628 6.15455909,16.1322314 8.59792727,12.3636364 C11.0412954,8.59504132 14.8590582,6.00413223 19.2113078,5.14049587 C20.3566366,4.90495868 21.4256102,4.82644628 22.570939,4.82644628 C30.5118856,4.82644628 37.1547929,10.4793388 38.8346085,18.6446281 C39.4454505,21.4710744 39.2163848,24.4545455 38.3001217,27.4380165 Z M22.3418733,12.5206612 C17.3787817,12.5206612 13.3319531,16.6818182 13.3319531,21.785124 C13.3319531,26.8884298 17.3787817,31.0495868 22.3418733,31.0495868 C27.3049649,31.0495868 31.3517934,26.8884298 31.3517934,21.785124 C31.3517934,16.6818182 27.3049649,12.5206612 22.3418733,12.5206612 Z M22.3418733,28.1446281 C18.9058868,28.1446281 16.1570976,25.3181818 16.1570976,21.785124 C16.1570976,18.2520661 18.9058868,15.4256198 22.3418733,15.4256198 C25.7778598,15.4256198 28.526649,18.2520661 28.526649,21.785124 C28.526649,25.3181818 25.7778598,28.1446281 22.3418733,28.1446281 Z"
              fill="#004E7D"
              fillRule="nonzero"
            ></path>
          </svg>
        </OpenButton>

        <h1>Find a f'real near you!</h1>

        <Form onSubmit={e => e.preventDefault()}>
          <Autocomplete
            onLoad={onAutocompleteLoad}
            onPlaceChanged={onPlaceChanged}
          >
            <input
              type="text"
              placeholder="Enter your zip code..."
              onFocus={e => {
                e.target.value = ""
                setHasInteracted(true)
              }}
              ref={el => {
                inputRef = el
              }}
            />
          </Autocomplete>
          <button onClick={submitForm} type="button">
            Go!
          </button>
        </Form>
      </Wrapper>
    )
  }

  const wrapperInput = () => {
    return (
      <Wrapper data-hidden={isHidden}>
        <CloseButton
          onClick={e => {
            setIsHidden(true)
          }}
        >
          &times;
        </CloseButton>

        <OpenButton
          data-visible={isHidden}
          onClick={e => {
            setIsHidden(false)
          }}
        >
          <svg
            width="45px"
            height="65px"
            viewBox="0 0 45 65"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
          >
            <ellipse
              className="shadow"
              fill="#7AACC7"
              cx="22.5"
              cy="59"
              rx="12.5"
              ry="4"
            ></ellipse>
            <path
              className="marker"
              d="M41.5833977,18.0950413 C39.6745163,8.59504132 31.809925,2 22.570939,2 C21.2728997,2 19.9748604,2.15702479 18.676821,2.39256198 C13.5610189,3.41322314 9.20876931,6.39669421 6.3072696,10.8719008 C3.40576989,15.3471074 2.33679631,20.6859504 3.40576989,26.0247934 C4.09296719,29.5578512 5.46736179,32.8553719 6.76540113,35.6033058 C9.28512457,41.0991736 12.5684006,46.6735537 17.0733606,53.268595 C18.065979,54.6818182 19.0585973,56.0950413 20.1275709,57.5082645 C20.1275709,57.5082645 20.1275709,57.5082645 20.1275709,57.5867769 L20.3566366,57.9008264 C20.3566366,57.9008264 20.4329919,57.9793388 20.4329919,57.9793388 C20.9674787,58.6859504 21.7310312,59 22.4945838,59 C23.0290706,59 23.9453336,58.7644628 24.7852415,57.7438017 C27.8394517,53.661157 30.4355304,49.6570248 32.8788985,45.6528926 C35.6276878,41.0991736 38.987319,35.053719 41.0489109,28.3016529 C42.1178845,24.8471074 42.270595,21.392562 41.5833977,18.0950413 Z M38.3001217,27.4380165 C36.3148851,33.8760331 33.0316091,39.6859504 30.4355304,44.0826446 C28.0685174,48.0082645 25.4724388,51.8553719 22.570939,55.8595041 C22.570939,55.8595041 22.570939,55.9380165 22.4945838,55.9380165 L22.4182285,55.7809917 C22.4182285,55.7809917 22.4182285,55.7024793 22.3418733,55.7024793 L22.3418733,55.7024793 C21.349255,54.2892562 20.3566366,52.8760331 19.3640183,51.4628099 C14.9354135,45.0247934 11.7284927,39.607438 9.28512457,34.268595 C8.06344048,31.677686 6.76540113,28.6157025 6.15455909,25.3181818 C5.23829602,20.7644628 6.15455909,16.1322314 8.59792727,12.3636364 C11.0412954,8.59504132 14.8590582,6.00413223 19.2113078,5.14049587 C20.3566366,4.90495868 21.4256102,4.82644628 22.570939,4.82644628 C30.5118856,4.82644628 37.1547929,10.4793388 38.8346085,18.6446281 C39.4454505,21.4710744 39.2163848,24.4545455 38.3001217,27.4380165 Z M22.3418733,12.5206612 C17.3787817,12.5206612 13.3319531,16.6818182 13.3319531,21.785124 C13.3319531,26.8884298 17.3787817,31.0495868 22.3418733,31.0495868 C27.3049649,31.0495868 31.3517934,26.8884298 31.3517934,21.785124 C31.3517934,16.6818182 27.3049649,12.5206612 22.3418733,12.5206612 Z M22.3418733,28.1446281 C18.9058868,28.1446281 16.1570976,25.3181818 16.1570976,21.785124 C16.1570976,18.2520661 18.9058868,15.4256198 22.3418733,15.4256198 C25.7778598,15.4256198 28.526649,18.2520661 28.526649,21.785124 C28.526649,25.3181818 25.7778598,28.1446281 22.3418733,28.1446281 Z"
              fill="#004E7D"
              fillRule="nonzero"
            ></path>
          </svg>
        </OpenButton>

        <h1>Find a f'real near you!</h1>

        <Form onSubmit={e => e.preventDefault()}>
          <input
            type="text"
            placeholder="Enter your zip code..."
            readOnly={true}
          />
          <button onClick={e => e.preventDefault()} type="button" disabled>
            Go!
          </button>
        </Form>
      </Wrapper>
    )
  }

  return isLoaded ? renderInput() : wrapperInput()
}
